// web/src/components/CoverLine.tsx
// The big declarative headline that drops onto a CoverHero photo — like
// "DEBUT GOAL" or "RHAPSODY IN BLUE" or "5-2 ROUT". Bold italic condensed
// sans, all-caps, scale based on prop.
//
// Pair with `coverLineFor(match)` in utils/cover-line.ts to derive the text
// + swatch colour from match data.

import type { CSSProperties } from 'react';

export interface CoverLineProps {
  text: string;
  /** Visual size; default 'display'. 'mega' for splash pages. */
  size?: 'compact' | 'display' | 'mega';
  /** Text colour (defaults to ink-on-green for dark hero placements). */
  color?: string;
  /** Optional kicker rendered above the cover line. */
  kicker?: string;
  align?: 'left' | 'center';
  style?: CSSProperties;
}

const SIZE_TO_FONT: Record<NonNullable<CoverLineProps['size']>, string> = {
  compact: '2rem',
  display: 'var(--display)',
  mega: 'var(--mega)',
};

export function CoverLine({
  text,
  size = 'display',
  color = 'var(--ink-on-green)',
  kicker,
  align = 'left',
  style,
}: CoverLineProps) {
  return (
    <div style={{ textAlign: align, color, ...style }}>
      {kicker ? (
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--micro)',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            opacity: 0.8,
            marginBottom: 'var(--space-xs)',
          }}
        >
          {kicker}
        </div>
      ) : null}
      <div
        style={{
          fontFamily: 'var(--font-masthead)',
          fontStyle: 'italic',
          fontSize: SIZE_TO_FONT[size],
          lineHeight: 0.95,
          letterSpacing: '-0.01em',
          textTransform: 'uppercase',
          textShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}
      >
        {text}
      </div>
    </div>
  );
}
